import { usePathname } from 'next/navigation';
import { useIntl } from 'react-intl';

import { getPurchaseError } from '@/components/purchase/utils/getPurchaseError';
import { useAnalytics } from '@/hooks/analytics';
import { useApi } from '@/hooks/api';
import { useFreeTrial } from '@/hooks/purchase/useFreeTrial';
import { usePricesState, usePurchaseParamsState, useUserState } from '@/hooks/store';
import { formatError } from '@/utils/formatErrors';
import type { JSONObject } from '@/utils/types';

import type { UseSubmitPaymentRequestProps } from '.';
import { isOneTimePurchaseResponse, isSubscriptionResponse, type PurchaseRequestResponse } from '../types';
import { createPurchaseBodyArgs } from '../utils';

export type UseSubmitClassicPaymentRequestProps = Pick<
	UseSubmitPaymentRequestProps,
	| 'analyticsEventProps'
	| 'analyticsPrefix'
	| 'endpoint'
	| 'hasTrueMedSupport'
	| 'purchaseArgs'
	| 'oneTimePurchaseSuccess'
	| 'onPurchaseError'
	| 'onPurchaseSuccess'
	| 'onPurchaseSuccessOverride'
	| 'onRequiresAction'
	| 'selectedPaymentType'
	| 'setIsLoading'
	| 'setSuccessfullySubmitted'
>;

export default function useSubmitClassicPaymentRequest({
	analyticsEventProps,
	analyticsPrefix,
	endpoint,
	hasTrueMedSupport,
	oneTimePurchaseSuccess,
	onPurchaseError,
	onPurchaseSuccess,
	onPurchaseSuccessOverride,
	onRequiresAction,
	purchaseArgs,
	selectedPaymentType,
	setIsLoading,
	setSuccessfullySubmitted,
}: UseSubmitClassicPaymentRequestProps) {
	const prices = usePricesState();
	const purchaseParams = usePurchaseParamsState();
	const user = useUserState();
	const apiRequest = useApi();
	const { formatMessage } = useIntl();
	const pathname = usePathname();
	const { logEvent } = useAnalytics();
	const userCanTrial = useFreeTrial();

	return async function submitClassicPaymentRequest(
		paymentTypeSpecificArgs: Record<string, unknown>,
		eventProps: JSONObject = {},
	) {
		try {
			const purchaseBodyArgs = createPurchaseBodyArgs({
				purchaseParams,
				prices,
				user,
				userCanTrial,
				purchaseArgs: {
					isStripeNextEnrolled: true,
					isPartner: pathname?.includes('partnership'),
					...purchaseArgs,
					...paymentTypeSpecificArgs,
					...(hasTrueMedSupport && {
						trueMedLink: `https://app.truemed.com/qualify/${process.env.NEXT_PUBLIC_TRUEMED_PUBLIC_QUALIFICATION_ID}?user_id=${user?.id}`,
					}),
				},
			});

			const _endpoint = typeof endpoint === 'string' ? endpoint : endpoint(purchaseBodyArgs);
			const { data } = await apiRequest<PurchaseRequestResponse>({
				endpoint: _endpoint,
				method: 'POST',
				body: purchaseBodyArgs,
				excludedHeaders: _endpoint === 'subscription/email' ? ['x-session-token'] : [],
			});

			logEvent({
				eventName: `${analyticsPrefix} : Posted`,
				eventProps: {
					endpoint: _endpoint,
					payment_type: selectedPaymentType,
					is_stripe_next: true,
					...eventProps,
					...analyticsEventProps,
				},
			});

			if (onPurchaseSuccessOverride) {
				await onPurchaseSuccessOverride(data, purchaseBodyArgs);
				if (!isOneTimePurchaseResponse(data)) {
					return;
				}
			}

			if (isSubscriptionResponse(data)) {
				await onPurchaseSuccess(data, eventProps);
			}
			if (isOneTimePurchaseResponse(data)) {
				await oneTimePurchaseSuccess(data, eventProps);
			}
		} catch (err) {
			// err.data is only present for a rejected HTTP response; a network failure
			// (no connection, DNS error, timeout) rejects with the raw error instead, so
			// fall back to its own code/message rather than losing them to observability.
			const error = err?.data?.error ?? { code: err?.code, message: err?.message };
			if (error.code === 'requires_action') {
				await onRequiresAction(error.info);
			} else {
				const sanitizedError = {
					...error,
					calmCode: error?.calmCode || error?.code,
				};
				onPurchaseError(
					formatMessage(getPurchaseError(error)),
					{ ...formatError(sanitizedError), ...eventProps },
					sanitizedError.calmCode,
				);
			}
		} finally {
			setIsLoading(false);
			setSuccessfullySubmitted(true);
		}
	};
}
